import React, { useState, useEffect, useMemo } from 'react';
import '../../component-styles/ApplyCreditModal.css';
import API_BASE from '../../utils/api.js';
import { roundCurrency, sanitizeCurrencyInput } from '../../utils/currency.js';
import notify from '../../utils/notify';

/**
 * ApplyFundsModal
 * - Apply Credit or Retainer to an Invoice, Proposal, Purchase Order, or TB Invoice
 * - Invoices/Proposals use /api/{type}/:projectId/:id/apply-payment
 * - Funds system is NOT used for invoices
 */
export default function ApplyFundsModal({
  projectId,
  targetId, // invoiceId or tbInvoiceId
  targetType, // "invoice" | "proposal" | "purchase-order" | "timebilling"
  targetRemaining,
  onClose,
  onApplied,
}) {
  const userId = localStorage.getItem('userId');
  const token = localStorage.getItem('idToken');

  const [sources, setSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(true);

  /* ==========================================================
     LOAD AVAILABLE CREDITS + RETAINERS
  ========================================================== */
  useEffect(() => {
    async function loadSources() {
      try {
        setLoading(true);

        const [creditsRes, retainersRes] = await Promise.all([
          fetch(`${API_BASE}/api/credits/${projectId}`, {
            headers: {
              userid: userId,
              Authorization: `Bearer ${token}`,
            },
          }),
          fetch(`${API_BASE}/api/retainers/${projectId}`, {
            headers: {
              userid: userId,
              Authorization: `Bearer ${token}`,
            },
          }),
        ]);

        const creditsJson = await creditsRes.json();
        const retainersJson = await retainersRes.json();

        const credits = (Array.isArray(creditsJson) ? creditsJson : creditsJson.Items || [])
          .filter((c) => Number(c.availableBalance) > 0)
          .map((c) => ({
            sourceType: 'credit',
            sourceId: c.creditId, // ✅ correct ID
            label: c.creditNumber,
            availableBalance: roundCurrency(c.availableBalance),
          }));

        const retainers = (Array.isArray(retainersJson) ? retainersJson : retainersJson.Items || [])
          .filter((r) => {
            const funded = Number(r.payments || 0);
            const applied = Number(r.appliedAmount || 0);
            const available = Number.isFinite(Number(r.availableBalance))
              ? roundCurrency(r.availableBalance)
              : roundCurrency(Math.max(funded - applied, 0));
            return available > 0;
          })
          .map((r) => ({
            sourceType: 'retainer',
            sourceId: r.retainerId, // ✅ correct ID
            label: r.retainerNumber,
            availableBalance: Number.isFinite(Number(r.availableBalance))
              ? roundCurrency(r.availableBalance)
              : roundCurrency(Math.max(Number(r.payments || 0) - Number(r.appliedAmount || 0), 0)),
          }));

        setSources([...credits, ...retainers]);
      } catch (err) {
        console.error('Failed to load funds:', err);
      } finally {
        setLoading(false);
      }
    }

    if (projectId && userId && token) {
      loadSources();
    }
  }, [projectId, userId, token, API_BASE]);

  /* ==========================================================
     MAX ALLOWED
  ========================================================== */
  const maxAllowed = useMemo(() => {
    if (!selectedSource) return 0;
    return roundCurrency(
      Math.min(roundCurrency(selectedSource.availableBalance), roundCurrency(targetRemaining || 0))
    );
  }, [selectedSource, targetRemaining]);

  /* ==========================================================
     APPLY FUNDS
  ========================================================== */
  async function applyFunds() {
    if (!selectedSource) return;

    const amt = roundCurrency(amount);
    if (!amt || amt <= 0) return;

    try {
      let url = '';
      let body = {};

      /* ======================================================
         INVOICES / PROPOSALS — USE DOC ROUTE
      ====================================================== */
      if (
        targetType === 'invoice' ||
        targetType === 'proposal' ||
        targetType === 'purchase-order'
      ) {
        url =
          targetType === 'invoice'
            ? `${API_BASE}/api/invoices/${projectId}/${targetId}/apply-payment`
            : targetType === 'proposal'
              ? `${API_BASE}/api/proposals/${projectId}/${targetId}/apply-payment`
              : `${API_BASE}/api/purchase-orders/${projectId}/${targetId}/apply-payment`;

        body = {
          amount: amt,
          paymentType: selectedSource.sourceType, // "credit" | "retainer"
          reference: selectedSource.sourceId, // ✅ creditId OR retainerId
          method: selectedSource.sourceType === 'credit' ? 'Credit' : 'Retainer',
        };
      } else {
        /* ======================================================
           NON-INVOICE TARGETS (TB, etc.)
        ====================================================== */
        url = `${API_BASE}/api/funds/apply`;

        body = {
          userId,
          projectId,
          sourceType: selectedSource.sourceType,
          sourceId: selectedSource.sourceId,
          targetType,
          targetId,
          amount: amt,
        };
      }

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          userid: userId,
        },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok) {
        notify(data.error || 'Failed to apply funds');
        return;
      }

      onApplied?.(data);
      onClose();
    } catch (err) {
      console.error('Apply funds error:', err);
      notify('Failed to apply funds');
    }
  }

  /* ==========================================================
     RENDER
  ========================================================== */
  return (
    <div className="ac-overlay" onClick={onClose}>
      <div className="ac-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Apply Funds</h3>

        {loading ? (
          <div className="ac-loading">Loading available funds…</div>
        ) : sources.length === 0 ? (
          <div className="ac-empty">No available credits or retainers.</div>
        ) : (
          <>
            <label>Choose a Credit or Retainer</label>
            <select
              value={selectedSource?.sourceId || ''}
              onChange={(e) => {
                const src = sources.find((s) => s.sourceId === e.target.value);
                setSelectedSource(src || null);
                setAmount('');
              }}
            >
              <option value="">Select a source…</option>
              {sources.map((s) => (
                <option key={`${s.sourceType}-${s.sourceId}`} value={s.sourceId}>
                  {s.label} ({s.sourceType}) — ${s.availableBalance.toFixed(2)}
                </option>
              ))}
            </select>

            {selectedSource && (
              <>
                <label>Amount to Apply</label>
                <input
                  type="number"
                  step="0.01"
                  value={amount}
                  min={0}
                  max={maxAllowed}
                  onChange={(e) => {
                    let val = sanitizeCurrencyInput(e.target.value);
                    if (val === '') {
                      setAmount('');
                      return;
                    }
                    val = String(Math.min(roundCurrency(val), maxAllowed));
                    setAmount(val);
                  }}
                />

                <p className="ac-max-note">Max Allowed: ${maxAllowed.toFixed(2)}</p>
              </>
            )}

            <div className="ac-actions">
              <button className="ac-cancel" onClick={onClose}>
                Cancel
              </button>
              <button
                className="ac-apply"
                disabled={!selectedSource || !amount}
                onClick={applyFunds}
              >
                Apply
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
